import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, func, shape, string } from 'prop-types'
import { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { PHONE_NUMBER_MODAL } from 'shared/consts/checkout'
import {
  changeAddressAction,
  hasCartRxEntries,
  hasPhoneNumber,
  setShippingAddressAction,
} from 'shared/store-checkout/duck/address'
import { showModalDeclarativeAction } from 'shared/store/ducks/events'
import Button from 'views/components/atoms/Button'
import IconPencil from 'views/components/atoms/Icon/IconPencil'
import InputCheckbox from 'views/components/molecules/InputCheckbox'
import ModalContext from 'views/components/molecules/Modal/ModalContext'
import AddressModal from 'views/components/organisms/AddressModal'
import PhonenumberModal from 'views/components/organisms/Checkout/PhonenumberModal'
import PrescriptionPhoneNumberModal from 'views/components/organisms/PrescriptionPhoneNumberModal'
import useMountPoints from 'views/enhancers/useMountPoints'

import './AddressListItem.scss'
import { getAddressName } from './helper'

const AddressListItem = ({
  subOrderId,
  entry,
  setSelectedAddressId,
  selectedAddressId,
  allowChanges,
  existingPhoneNumber,
}) => {
  const dispatch = useDispatch()
  const hasCartRx = useSelector(state => hasCartRxEntries(state))
  const hasPhone = useSelector(state => hasPhoneNumber(state))

  const { id, extension, pickupStation, street, streetNo, zip, city, type } =
    entry
  const selected = id === selectedAddressId
  const classString = classNames(
    'o-checkoutAddressBox',
    {
      'o-checkoutAddressBox--selected': selected,
    },
    'a-list-reset l-flex--start u-padding-large--sides  a-box--light '
  )

  const mountPoints = useMountPoints()

  const modalRef = useContext(ModalContext)

  const history = useHistory()

  // change phone of current address entry on modal submit
  const changePhoneNumber = e => phoneNumber => {
    e.preventDefault()
    const address = { ...entry }
    address.phone = phoneNumber
    dispatch(changeAddressAction(id, address))
    history.push(mountPoints.getCheckoutShipping())
  }

  const submitAddress = () => {
    if (modalRef !== null) {
      modalRef.hide()
    }

    dispatch(setShippingAddressAction(subOrderId, id))

    if (hasCartRx && !hasPhone) {
      dispatch(showModalDeclarativeAction(PHONE_NUMBER_MODAL))
    } else {
      history.push(mountPoints.getCheckoutShipping())
    }
  }

  const addressModalProps = {
    addressId: entry.id,
    modalId: 'update-shipping-address',
    size: 'medium',
    title: <Trans id="modal.address.title.modifyAddress" />,
    csrfToken: 'csrf-test-token',
    cartHasRXEntries: hasCartRx,
    type: entry.type,
    initialFormValues: entry,
    hasBillingAddress: false,
  }

  const rxPhoneModalProps = {
    id: 'checkout-prescription-phone',
    csrfToken: 'csrf-test-token',
    cartHasRXEntries: hasCartRx,
    modalId: 'checkout-prescription-phone',
    size: 'medium',
    isClientSideControlled: true,
  }

  const addressName = getAddressName(entry)

  const isRadioButtonOn = selected ? 'on' : ''

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
    <li
      className={classString}
      onClick={() => {
        setSelectedAddressId(id)
      }}
    >
      <ul className="l-flex l-flex__start l-flex--stretch-children a-list-reset">
        <li
          className="l-flex__start u-padding-large--top"
          key={`checkbox-${id}`}
        >
          <InputCheckbox
            id={`shippingAddress-${id}`}
            type="radio"
            name="selectedAddress"
            value={isRadioButtonOn}
            readOnly
          >
            &nbsp;
          </InputCheckbox>
        </li>
        <li className="a-box l-flex__start" key={`address-${id}`}>
          <b>{addressName}</b>
          <br />
          {street} {streetNo} <br />
          {extension && (
            <>
              {extension} <br />
            </>
          )}
          {zip} {city}
          {existingPhoneNumber && type === 'billing' && (
            <>
              <br />
              {existingPhoneNumber}
            </>
          )}
        </li>
      </ul>
      {selected && (
        <>
          <ul className="a-list-reset l-flex l-flex--stretch-children l-flex--vertical-stretch@small-tiny u-margin-small--bottom">
            <li
              className="u-margin-small--bottom l-flex--vertical-stretch l-flex__primary"
              key={`deliver-button-${id}`}
            >
              <Button onClick={submitAddress} name="use-shipping-address">
                <Trans id="checkout.address.deliverToThisAddress" />
              </Button>
              <PhonenumberModal />
            </li>
            {!pickupStation && allowChanges && (
              <li
                className="u-margin-small--bottom@small-tiny u-margin-small--left@medium"
                key={`update-button-${id}`}
              >
                <Button
                  style="buttonGhost"
                  name="update-shipping-address"
                  onClick={() => {
                    dispatch(
                      showModalDeclarativeAction(addressModalProps.modalId)
                    )
                  }}
                >
                  <div className="o-AddressListItem__ButtonText">
                    <Trans id="checkout.address.updateDeliveryAddress" />
                  </div>
                  <IconPencil className="o-AddressListItem__ButtonIcon" />
                </Button>
              </li>
            )}
          </ul>
          {allowChanges && <AddressModal {...addressModalProps} />}
          <PrescriptionPhoneNumberModal
            handlePhoneNumberChange={changePhoneNumber}
            {...rxPhoneModalProps}
          />
        </>
      )}
    </li>
  )
}

AddressListItem.propTypes = {
  subOrderId: string,
  entry: shape({}).isRequired,
  setSelectedAddressId: func.isRequired,
  selectedAddressId: string.isRequired,
  allowChanges: bool,
  existingPhoneNumber: string,
}

AddressListItem.defaultProps = {
  subOrderId: '',
  allowChanges: false,
  existingPhoneNumber: '',
}

export default AddressListItem
